"use client";

import { useState } from "react";
import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  DECIMAL_PLACES,
  type DecimalPlaces,
} from "@/components/tools/geometry/format";
import {
  ForwardView,
  InverseView,
} from "@/components/tools/geometry/trig-views";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import type { AngleInputUnit } from "@/lib/tool-kit/geometry/angle-input";
import {
  INVERSE_FUNCTIONS,
  type InverseFunction,
} from "@/lib/tool-kit/geometry/trig-solve";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";

type Mode = "forward" | "inverse";

/** English strings. A translated content file overrides these through `ui`. */
export const TRIGONOMETRY_CALCULATOR_STRINGS = {
  modeLabel: "What do you want to find?",
  modes: {
    forward: "The six trig functions of an angle",
    inverse: "An angle from a trig value (inverse functions)",
  },
  angleLabel: "Angle",
  angleUnitLabel: "Angle unit",
  angleUnits: { deg: "Degrees", rad: "Radians" },
  angleHint:
    "Any angle, negative or larger than a full turn. In radians, type a multiple of π such as 5pi/6 to keep the answer exact.",
  inverseLabel: "Inverse function",
  inverseFunctions: {
    asin: "arcsin (sin⁻¹)",
    acos: "arccos (cos⁻¹)",
    atan: "arctan (tan⁻¹)",
    acsc: "arccsc (csc⁻¹)",
    asec: "arcsec (sec⁻¹)",
    acot: "arccot (cot⁻¹)",
  },
  valueLabel: "Value",
  valueHint:
    "A decimal, a fraction such as 1/2, or a radical such as √3/2 or sqrt(2)/2 — radicals keep the angle exact.",
  decimalsLabel: "Decimal places",
  results: "Results",
  functionHeader: "Function",
  exactHeader: "Exact",
  decimalHeader: "Decimal",
  functionNames: {
    sin: "sin θ",
    cos: "cos θ",
    tan: "tan θ",
    csc: "csc θ",
    sec: "sec θ",
    cot: "cot θ",
  },
  undefinedText: "undefined",
  angleCard: "Angle",
  coterminalCard: "Coterminal angle (0° to 360°)",
  referenceCard: "Reference angle",
  quadrantCard: "Quadrant",
  onAxis: "On an axis",
  quadrantNames: { 1: "I", 2: "II", 3: "III", 4: "IV" },
  positiveIn: {
    1: "All six functions are positive in quadrant I.",
    2: "In quadrant II, sine and cosecant are positive; the other four are negative.",
    3: "In quadrant III, tangent and cotangent are positive; the other four are negative.",
    4: "In quadrant IV, cosine and secant are positive; the other four are negative.",
  },
  axisNote:
    "An angle on an axis belongs to no quadrant, and some functions are undefined there.",
  workingHeading: "The working",
  figureHeading: "Figure",
  figureTitle:
    "The angle {angle} on the unit circle, with the cosine, sine and tangent lengths marked",
  downloadFigure: "Download figure (PNG)",
  degreesCard: "In degrees",
  radiansCard: "In radians",
  ranges: {
    asin: "arcsin returns an angle from −90° to 90°.",
    acos: "arccos returns an angle from 0° to 180°.",
    atan: "arctan returns an angle between −90° and 90°, not including them.",
    acsc: "arccsc returns an angle from −90° to 90°, excluding 0°.",
    asec: "arcsec returns an angle from 0° to 180°, excluding 90°.",
    acot: "arccot returns an angle between 0° and 180°, not including them (the usual calculus convention).",
  },
  allHeading: "All solutions from 0° to 360°",
  generalHeading: "General solution",
  generalOne:
    "θ = {first} + 360°k or θ = {second} + 360°k, for any whole number k.",
  generalTan: "θ = {first} + 180°k, for any whole number k.",
  generalSingle: "θ = {first} + 360°k, for any whole number k.",
  copy: "Copy results",
  copied: "Copied!",
  summaryForward: "{angle}: sin = {sin}, cos = {cos}, tan = {tan}",
  summaryInverse: "{fn}({value}) = {angle}",
  enter: "Enter a value to see the result.",
  invalidAngle:
    "Enter the angle as a number; in radians you can also write multiples of π, such as 5pi/6.",
  invalidValue:
    "Enter the value as a decimal, a fraction such as 1/2, or a radical such as √3/2.",
  domainAsin: "arcsin and arccos are only defined for values from −1 to 1.",
  domainCsc:
    "arccsc and arcsec are only defined for values that are at least 1 or at most −1.",
} as const;

export type TrigStrings = typeof TRIGONOMETRY_CALCULATOR_STRINGS;

const ID_PREFIX = "trig";

export default function TrigonometryCalculatorWidget() {
  const strings = useToolUi(TRIGONOMETRY_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const [mode, setMode] = useState<Mode>("forward");
  const [angleText, setAngleText] = useState("150");
  const [angleUnit, setAngleUnit] = useState<AngleInputUnit>("deg");
  const [inverse, setInverse] = useState<InverseFunction>("asin");
  const [valueText, setValueText] = useState("√3/2");
  const [places, setPlaces] = useState<DecimalPlaces>("4");

  return (
    <>
      <div className="space-y-4">
        <SelectField
          id={`${ID_PREFIX}-mode`}
          label={strings.modeLabel}
          onChange={setMode}
          options={(["forward", "inverse"] as const).map((id) => ({
            value: id,
            label: strings.modes[id],
          }))}
          value={mode}
        />
        {mode === "forward" ? (
          <div className="grid gap-4 sm:grid-cols-3">
            <TextField
              hint={strings.angleHint}
              id={`${ID_PREFIX}-angle`}
              label={strings.angleLabel}
              mono
              onChange={setAngleText}
              value={angleText}
            />
            <SelectField
              id={`${ID_PREFIX}-unit`}
              label={strings.angleUnitLabel}
              onChange={(next) => {
                setAngleUnit(next);
                setAngleText(next === "deg" ? "150" : "5pi/6");
              }}
              options={(["deg", "rad"] as const).map((id) => ({
                value: id,
                label: strings.angleUnits[id],
              }))}
              value={angleUnit}
            />
            <SelectField
              id={`${ID_PREFIX}-places`}
              label={strings.decimalsLabel}
              onChange={setPlaces}
              options={DECIMAL_PLACES.map((value) => ({ value, label: value }))}
              value={places}
            />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-3">
            <SelectField
              id={`${ID_PREFIX}-inverse`}
              label={strings.inverseLabel}
              onChange={setInverse}
              options={INVERSE_FUNCTIONS.map((id) => ({
                value: id,
                label: strings.inverseFunctions[id],
              }))}
              value={inverse}
            />
            <TextField
              hint={strings.valueHint}
              id={`${ID_PREFIX}-value`}
              label={strings.valueLabel}
              mono
              onChange={setValueText}
              value={valueText}
            />
            <SelectField
              id={`${ID_PREFIX}-places-inverse`}
              label={strings.decimalsLabel}
              onChange={setPlaces}
              options={DECIMAL_PLACES.map((value) => ({ value, label: value }))}
              value={places}
            />
          </div>
        )}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {mode === "forward" ? (
          <ForwardView
            angleText={angleText}
            angleUnit={angleUnit}
            fmt={fmt}
            mark={mark}
            places={Number(places)}
            strings={strings}
          />
        ) : (
          <InverseView
            fmt={fmt}
            fn={inverse}
            mark={mark}
            places={Number(places)}
            strings={strings}
            valueText={valueText}
          />
        )}
      </section>
    </>
  );
}
